import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, map, mergeMap, takeUntil } from 'rxjs';

import { CategoryContextFacade } from 'ish-core/facades/category-context.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ProductLinksDictionary } from 'ish-core/models/product-links/product-links.model';
import { ProductView } from 'ish-core/models/product-view/product-view.model';

import { ProductLinksCarouselComponent } from '../product-links-carousel/product-links-carousel.component';

/**
 * The Product Links Component
 *
 * Collects all types of product links and prepares their rendering.
 * Product links can be displayed as product list (uses {@link ProductLinkListComponent}) or product carousel (uses  {@link ProductLinksCarouselComponent}).
 * For the carousel swiper is used.
 *
 * @example
 * <ish-product-links></ish-product-links>
 */
@Component({
  selector: 'ish-product-links',
  templateUrl: './product-links.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProductLinksCarouselComponent, AsyncPipe, TranslateModule, CommonModule],
  providers: [CategoryContextFacade],
})
export class ProductLinksComponent implements OnInit, OnDestroy {
  private context = inject(ProductContextFacade);
  private categoryContext = inject(CategoryContextFacade);
  private gtmFacade = inject(GoogleTagManagerFacade);
  private checkoutFacade = inject(CheckoutFacade);
  private elementRef = inject(ElementRef);

  links$: Observable<ProductLinksDictionary>;
  triggerCount = 1;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.links$ = this.checkoutFacade.currentRouteUrl$.pipe(
      map(url => !!url && url.startsWith('/c/')),
      mergeMap(isCategoryPage => (isCategoryPage ? this.categoryContext.select('links') : this.context.select('links')))
    );

    this.links$.pipe(takeUntil(this.destroy$)).subscribe(item => {
      const gtmProduct: ProductView[] = item?.crossselling?.products
        .map((data: string) => data.split(',').map((sku: string) => ({ sku })))
        .flat();
      this.gtmFacade.setupIntersectionObserverForGTMEvent(
        this.elementRef.nativeElement,
        gtmProduct,
        this.triggerCount,
        'view_item_list'
      );
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
